import { Component, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { AuthStateService } from '../../services/auth-state.service';
import { AuthService } from '../../services/auth.service';
import { MenuService } from '../../services/menu.service';
import { LogoutLoadingService } from '../../services/logout-loading.service';
import { EncryptionService } from '../../services/encryption.service';
import { Subscription, catchError, EMPTY } from 'rxjs';
import { filter } from 'rxjs/operators';

// Angular Material imports
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { IonText, IonIcon, IonPopover, IonItem, IonLabel, IonAvatar } from "@ionic/angular/standalone";
import { StorageService } from '../../services/storage.service';
import { TenantService } from '../../services/tenant.service';
import { SnackbarService } from '../../services/snackbar.service';
 
@Component({
  selector: 'app-header',
  imports: [IonLabel, IonItem, IonPopover, IonIcon, IonText,
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    MatToolbarModule,
    MatTooltipModule,
    MatIconModule,
    MatDividerModule, IonAvatar],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  isLoggedIn = false;
  representativeName: any = '';
  patientName: any = '';
  isPatientRepresentative = false;
  userGender: string = ''; // Store user gender ('0' = Male, '1' = Female, '2' = Other)
  private storageService = inject(StorageService);
  private tenantService = inject(TenantService);
  private snackbarService = inject(SnackbarService);
  showLogo = true;
  showPracticeName = true;
  isMenuOpen = false;
  currentMenuName: string = '';
  logoUrl: string | null = null;
  tenantName: string | null = null;
  private subscription: Subscription = new Subscription();
  isDesktop: boolean;
  breakpoint: any = window.matchMedia('(min-width: 1025px)');
  private isDestroyed = false;

  @ViewChild('profilePopoverDesktop') private profilePopoverDesktop?: IonPopover;
  @ViewChild('profilePopoverMobile') private profilePopoverMobile?: IonPopover;
  private popstateListener?: () => void;
  private breakpointChangeListener?: () => void;
  
  constructor(
    private authStateService: AuthStateService,
    private authService: AuthService,
    private router: Router,
    private menuService: MenuService,
    private logoutLoadingService: LogoutLoadingService,
    private encryptionService: EncryptionService
  ) {
     this.isDesktop = !!this.breakpoint.matches;
     // QP-FE-ML-002 — keep isDesktop in sync on viewport resize
     this.breakpointChangeListener = () => {
       this.isDesktop = !!this.breakpoint.matches;
     };
     this.breakpoint.addEventListener('change', this.breakpointChangeListener);
   }

  ngOnInit(): void {
    // Subscribe to menu state changes
    this.subscription.add(
      this.menuService.isCollapsed$.subscribe(isCollapsed => {
        // On mobile, menu is open when not collapsed
        this.isMenuOpen = !isCollapsed;
      })
    );
    
    // Subscribe to authentication state
    this.subscription.add(
      this.authStateService.isLoggedIn$.subscribe(isLoggedIn => {
        this.isLoggedIn = isLoggedIn;
        if (isLoggedIn) {
          this.loadUserInfo();
        }
      })
    );
    
    this.subscription.add(
      this.authStateService.userInfo$.subscribe(userInfo => {
        if (userInfo) {
          this.loadUserInfo();
        }
      })
    );
    
    // Set practice name and logo settings
    // These could come from a configuration service in the future
    this.showLogo = true;
    this.showPracticeName = true;
    
    // Track current route and update menu name
    this.updateMenuName();
    
    // Subscribe to route changes
    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.updateMenuName();
        })
    );
    // Close profile popovers when route changes (back button or navigation)
    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationStart))
        .subscribe(() => this.closeAllPopovers())
    );
    this.setupPopstateListener();
    
    // Fetch logo URL from API
    this.fetchLogoUrl();
    
    // Get tenant name for fallback
    this.loadTenantName();
  }
  
  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.breakpointChangeListener) {
      this.breakpoint.removeEventListener('change', this.breakpointChangeListener);
      this.breakpointChangeListener = undefined;
    }
    if (this.popstateListener) {
      window.removeEventListener('popstate', this.popstateListener);
      this.popstateListener = undefined;
    }
    this.subscription.unsubscribe();
  }

  /**
   * Close all profile popovers (used when route changes or back button is pressed)
   */
  private closeAllPopovers(): void {
    void this.profilePopoverDesktop?.dismiss();
    void this.profilePopoverMobile?.dismiss();
  }

  /** Wait until profile popovers are closed before continuing (e.g. logout). */
  private async dismissProfilePopovers(): Promise<void> {
    await Promise.all([
      this.profilePopoverDesktop?.dismiss().catch(() => false) ?? Promise.resolve(false),
      this.profilePopoverMobile?.dismiss().catch(() => false) ?? Promise.resolve(false)
    ]);
  }

  private setupPopstateListener(): void {
    this.popstateListener = () => this.closeAllPopovers();
    window.addEventListener('popstate', this.popstateListener);
  }
  
  /**
   * Load user information from auth state service
   */
  loadUserInfo(): void {
    const userInfo = this.storageService.getSessionStorage('user_info');
    
    if (!userInfo) {
      this.representativeName = '';
      this.patientName = '';
      this.isPatientRepresentative = false;
      this.userGender = '';
      return;
    }
    
    // Store gender from user info
    this.userGender = userInfo.patientGender || '';
    
    // Check if this is a patient representative:
    // 1. Has patientCode or patientEmail (set when a patient is selected)
    // 2. OR has associated_patients in storage (before patient selection)
    // 3. OR has representativeName set (indicates patient representative from login)
    const hasAssociatedPatients = !!this.storageService.getSessionStorage('associated_patients');
    this.isPatientRepresentative = !!(userInfo.patientCode || userInfo.patientEmail || hasAssociatedPatients || userInfo.representativeName);
    
    if (this.isPatientRepresentative) {
      // For patient representatives: show both the logged-in user's name and the patient's name
      this.representativeName = userInfo.representativeName || userInfo.userName || '';
      this.patientName = userInfo.patientName || '';
    } else {
      // For normal users: show just the patient name (which is the same as their name)
      this.representativeName = userInfo.patientName || userInfo.userName || '';
      this.patientName = '';
    }
  }

  /**
   * Open help info document from API in a new tab
   * Opens window immediately to avoid iOS Safari popup blocking
   */
  openHelpInfoDoc(): void {
    // Get tenant code from current tenant or session storage
    const currentTenant = this.tenantService.getCurrentTenant();
    const tenantCode = currentTenant?.tenantCode || this.storageService.getSessionStorage('tenant_code');
    
    if (!tenantCode) {
      console.error('Tenant code not found');
      this.snackbarService.showError('Unable to load help document. Tenant information not found.');
      return;
    }
    
    // Open window immediately to prevent iOS Safari popup blocking
    // This must happen synchronously within the click event handler
    const newWindow = window.open('about:blank', '_blank');
    
    // QP-FE-ML-001 — track help-doc request so it is cancelled when header is destroyed
    this.subscription.add(
      this.authService.getHelpInfoDoc(tenantCode).subscribe({
      next: (response: any) => {
        // Check if signurl exists and is not empty
        if (response && response.signurl && response.signurl.trim() !== '') {
          // Update the window location with the actual URL
          if (newWindow) {
            newWindow.location.href = response.signurl;
          }
        } else {
          // Close the blank window if no URL is available
          if (newWindow) {
            newWindow.close();
          }
          // Show error message when document is not found
          const errorMessage = response?.message || 'Help info document not found';
          this.snackbarService.showWarning(errorMessage);
        }
      },
      error: (error: any) => {
        // Close the blank window on error
        if (newWindow) {
          newWindow.close();
        }
        console.error('Error fetching help info document:', error);
        const errorMessage = error?.error?.message || 'Unable to load help document. Please try again later.';
        this.snackbarService.showError(errorMessage);
      }
    })
    );
  }

  /**
   * Toggle the side menu
   */
  toggleMenu(): void {
    this.menuService.toggleSideMenu();
  }

  /**
   * Navigate to a specific route
   */
  navigateTo(route: string): void {
    this.router.navigate([route]);
  }

  /**
   * Update the current menu name based on the active route
   */
  private updateMenuName(): void {
    const currentRoute = this.router.url.split('?')[0];
    
    // Map routes to menu display names
    if (currentRoute === '/home' || currentRoute.startsWith('/home')) {
      this.currentMenuName = 'Home';
    } else if (currentRoute === '/messages' || currentRoute.startsWith('/messages')) {
      this.currentMenuName = 'My Messages';
    } else if (currentRoute === '/ask-provider' || currentRoute.startsWith('/ask-provider')) {
      this.currentMenuName = 'Ask a Provider';
    } else if (currentRoute === '/appointments' || currentRoute.startsWith('/appointments')) {
      this.currentMenuName = 'Appointments';
    } else if (currentRoute === '/health-record/health-information' ||
               currentRoute.startsWith('/health-record/health-information')) {
      this.currentMenuName = 'Health Information';
    } else if (currentRoute === '/health-record/visit-summary' ||
               currentRoute.startsWith('/health-record/visit-summary')) {
      this.currentMenuName = 'Visit Summary';
    } else if (currentRoute === '/health-record/exam-notes' ||
               currentRoute.startsWith('/health-record/exam-notes')) {
      this.currentMenuName = 'Exam Notes';
    } else if (currentRoute === '/health-record' || currentRoute.startsWith('/health-record')) {
      this.currentMenuName = 'Health Records';
    } else if (currentRoute === '/patient-forms' || currentRoute.startsWith('/patient-forms')) {
      this.currentMenuName = 'Patient Forms';
    } else if (currentRoute === '/pay-bills' || currentRoute.startsWith('/pay-bills')) {
      this.currentMenuName = 'Pay Bills';
    } else if (currentRoute === '/statements' || currentRoute.startsWith('/statements')) {
      this.currentMenuName = 'View Statements';
    } else if (currentRoute === '/profile' || currentRoute.startsWith('/profile')) {
      this.currentMenuName = 'My Profile';
    } else if (currentRoute === '/audit-log' || currentRoute.startsWith('/audit-log')) {
      this.currentMenuName = 'My Access Log';
    } else if (currentRoute === '/restriction' || currentRoute.startsWith('/restriction')) {
      this.currentMenuName = 'Restriction Request';
    } else if (currentRoute === '/phi/view' || currentRoute === '/phi/submit' || currentRoute.startsWith('/phi')) {
      this.currentMenuName = 'Patient Health Information';
    } else if (currentRoute === '/patientMsg' || currentRoute.startsWith('/patientMsg')) {
      this.currentMenuName = 'Patient Message';
    } else {
      this.currentMenuName = '';
    }
  }

  loadTenantName(): void {
    // Try to get tenant from TenantService first
    const currentTenant = this.tenantService.getCurrentTenant();
    if (currentTenant?.tenantName) {
      this.tenantName = currentTenant.tenantName;
      return;
    }

    // If not available, try to load by tenant code from storage
    const tenantCode = this.storageService.getSessionStorage('tenant_code');
    if (tenantCode) {
      this.subscription.add(
        this.tenantService.getTenantByCode(tenantCode).subscribe(tenant => {
          if (tenant?.tenantName) {
            this.tenantName = tenant.tenantName;
          }
        })
      );
    }
  }

  // PE-6337 — async AES-GCM encrypt for logo tenant code
  async fetchLogoUrl(): Promise<void> {
    // Get tenant code from sessionStorage (already decrypted by getSessionStorage)
    const tenantCode = this.storageService.getSessionStorage('tenant_code');

    if (!tenantCode) {
      // If no tenant code, don't set logoUrl (will show tenant name instead)
      return;
    }

    // Encrypt tenant code using AES-256-GCM before passing to API
    const encryptedTenantCode = await this.encryptionService.encryptAes256Gcm(tenantCode);

    // AuthService caches logo URL in memory (same as getTemplateMessageById) - no session storage
    this.subscription.add(
      this.authService.getLogoSignUrl(encryptedTenantCode)
        .pipe(
          catchError((error: any) => {
            console.error('Error fetching logo URL:', error);
            // Don't set logoUrl on error - will show tenant name instead
            this.logoUrl = null;
            return EMPTY;
          })
        )
        .subscribe((response: any) => {
          if (response && response.signurl) {
            this.logoUrl = response.signurl;
          } else {
            // Don't set logoUrl if signurl is not available - will show tenant name instead
            this.logoUrl = null;
          }
        })
    );
  }

  // Handle logo loading errors
  onLogoError(event: any): void {
    // Hide the image if it fails to load (will show tenant name instead)
    this.logoUrl = null;
    event.target.style.display = 'none';
  }

  /**
   * Logout the user and redirect to tenant-specific sign-in.
   * Dismiss the profile menu first so logout work does not freeze the open dropdown.
   */
  async logout(): Promise<void> {
    await this.dismissProfilePopovers();
    if (this.isDestroyed) {
      return;
    }

    this.logoutLoadingService.setLoading(true);

    try {
      await this.authService.logout();
    } catch (error: unknown) {
      console.error('Logout error:', error);
      if (!this.isDestroyed) {
        this.logoutLoadingService.setLoading(false);
      }
    }
  }
}
